/**
 * Phone bottom sheet. Below 768px the collapsed sidebar peeks its product
 * tiles; swipe the peek up (or tap the handle) to expand it. Tap the map to
 * collapse it again (app.js initSidebar).
 */

const SWIPE_SLOP = 24;

/** Decide what a completed drag on the sheet should do. */
export function sheetGesture({ dx, dy, collapsed, slop = SWIPE_SLOP }) {
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    if (adx < slop && ady < slop) return "tap";
    if (adx > ady) return "ignore";
    if (collapsed && dy < 0) return "expand";
    if (!collapsed && dy > 0) return "collapse";
    return "ignore";
}

/** Finger-follow offset, clamped to the closed and open positions. */
export function sheetDragY(base, dy, lockDy, max) {
    const y = base + (dy - lockDy);
    if (y < 0) return 0;
    if (y > max) return max;
    return y;
}

function isPhone() {
    return window.matchMedia("(max-width: 768px)").matches;
}

function measuredLength(value) {
    const probe = document.createElement("div");
    probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;height:${value}`;
    document.body.appendChild(probe);
    const height = probe.offsetHeight;
    probe.remove();
    return height;
}

function safeInsetBottom() {
    return measuredLength("env(safe-area-inset-bottom, 0px)");
}

export function installMobileSheet() {
    const sidebar = document.getElementById("sidebar");
    if (!sidebar || sidebar.querySelector(".rr-sheet-handle")) return;

    const handle = document.createElement("button");
    handle.type = "button";
    handle.className = "rr-sheet-handle";
    handle.addEventListener("click", () => sidebar.classList.toggle("collapsed"));
    sidebar.prepend(handle);

    const syncHandleLabel = () => {
        const collapsed = sidebar.classList.contains("collapsed");
        handle.setAttribute(
            "aria-label",
            collapsed ? "Swipe up or tap to expand controls" : "Swipe down or tap to collapse controls",
        );
    };
    syncHandleLabel();
    new MutationObserver(syncHandleLabel).observe(sidebar, { attributes: true, attributeFilter: ["class"] });

    let gesture = null;
    let ignoreClick = false;
    let settleSnap = null;

    const closedOffset = () => {
        const peek = measuredLength("var(--peek)");
        return Math.max(0, sidebar.offsetHeight - peek - safeInsetBottom());
    };

    const armIgnoreClick = () => {
        ignoreClick = true;
        setTimeout(() => {
            ignoreClick = false;
        }, 400);
    };

    sidebar.addEventListener("click", (event) => {
        if (!ignoreClick) return;
        ignoreClick = false;
        event.preventDefault();
        event.stopPropagation();
    }, true);

    const snapSheet = (open) => {
        if (settleSnap) settleSnap();
        const target = open ? 0 : closedOffset();
        const dragging = sidebar.classList.contains("rr-dragging");
        if (!dragging) {
            sidebar.classList.toggle("collapsed", !open);
            return;
        }
        sidebar.classList.remove("rr-dragging");
        void sidebar.offsetWidth;
        sidebar.style.transform = `translateY(${target}px)`;
        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            settleSnap = null;
            sidebar.removeEventListener("transitionend", onEnd);
            sidebar.classList.toggle("collapsed", !open);
            sidebar.style.transform = "";
        };
        const onEnd = (event) => {
            if (event.target !== sidebar || event.propertyName !== "transform") return;
            settle();
        };
        settleSnap = settle;
        sidebar.addEventListener("transitionend", onEnd);
        setTimeout(settle, 450);
    };

    sidebar.addEventListener("pointerdown", (event) => {
        if (!isPhone() || event.isPrimary === false || event.button > 0) return;
        const collapsed = sidebar.classList.contains("collapsed");
        const onHandle = !!event.target.closest(".rr-sheet-handle");
        if (!collapsed && !onHandle) return;
        if (settleSnap) settleSnap();
        gesture = {
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            collapsed,
            locked: null,
            lockDy: 0,
            base: 0,
        };
    });

    window.addEventListener("pointermove", (event) => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!gesture.locked) {
            if (Math.abs(dx) < SWIPE_SLOP && Math.abs(dy) < SWIPE_SLOP) return;
            if (Math.abs(dx) > Math.abs(dy)) {
                gesture.locked = "h";
                return;
            }
            gesture.locked = "v";
            gesture.lockDy = dy;
            gesture.base = gesture.collapsed ? closedOffset() : 0;
            sidebar.classList.add("rr-dragging");
            void sidebar.offsetWidth;
        }
        if (gesture.locked !== "v") return;
        const y = sheetDragY(gesture.base, dy, gesture.lockDy, closedOffset());
        sidebar.style.transform = `translateY(${y}px)`;
    });

    const endGesture = (event, cancelled) => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const current = gesture;
        gesture = null;
        const dx = event.clientX - current.x;
        const dy = event.clientY - current.y;
        if (current.locked === "v") armIgnoreClick();
        const action = cancelled || current.locked === "h"
            ? "ignore"
            : sheetGesture({ dx, dy, collapsed: current.collapsed });
        const open = action === "expand" ? true : action === "collapse" ? false : !current.collapsed;
        snapSheet(open);
    };

    window.addEventListener("pointerup", (event) => endGesture(event, false));
    window.addEventListener("pointercancel", (event) => endGesture(event, true));
}
